import { useEffect } from 'react';
import { useSourcesStore, type SourceRecord } from '../store/useSourcesStore';

/**
 * URL for the source library. Served from `app/public/manifest/sources.json`
 * (synced from `.planning/sources/sources.json` by Plan 04-01; 89 records at
 * the time of writing).
 */
const SOURCES_URL = '/manifest/sources.json';

/**
 * `useSources` — fetches sources.json once per app lifetime and populates
 * `useSourcesStore`. Plan 04-03 Task 1.
 *
 * Mounted once at App root (Task 4 adds a tiny `<SourcesLoader />` wrapper)
 * so that when the first selection happens the Sources panel can render
 * synchronously from the store (target: <200ms click-to-render).
 *
 * Shape handling:
 *   - Array-of-records:               `[{id: ...}, ...]`
 *   - Wrapped (current schema v1.0):  `{version: '1.0', sources: [...]}`
 * Both are accepted; unknown shapes throw and the catch handler logs + keeps
 * `loaded=false` so a future retry (e.g. Suspense boundary remount) can run
 * without the single-flight guard blocking it.
 *
 * Error handling: HTTP non-OK and shape errors are logged via `console.error`
 * but never throw — the 3D scene should continue to work even if sources.json
 * fails to load; the Sources panel will simply render the "no citations" fallback
 * for every object.
 *
 * The single-flight guard is keyed on `useSourcesStore.loaded`. React StrictMode
 * double-invokes effects, and this guard handles that cleanly: the second
 * invocation sees loaded=true and returns early.
 */
export function useSources(): { loaded: boolean } {
  const loaded = useSourcesStore((s) => s.loaded);
  const setRecords = useSourcesStore((s) => s.setRecords);

  useEffect(() => {
    if (loaded) return; // single-flight guard — also covers StrictMode double-mount
    let cancelled = false;
    fetch(SOURCES_URL)
      .then((r) => {
        if (!r.ok) {
          throw new Error(`Sources fetch failed: ${r.status} ${r.statusText}`);
        }
        return r.json();
      })
      .then((data: SourceRecord[] | { version: string; sources: SourceRecord[] }) => {
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : data?.sources;
        if (!Array.isArray(arr)) {
          throw new Error('Sources payload shape invalid — expected array or {sources: [...]}');
        }
        setRecords(arr);
      })
      .catch((e) => {
        if (!cancelled) console.error('[useSources]', e);
      });
    return () => {
      cancelled = true;
    };
  }, [loaded, setRecords]);

  return { loaded };
}
